/**
 * Recorder ring buffers and raw data ingestion/clearing.
 *
 * Kept strictly decoupled from presentation lifecycles, solvers, and reports
 * so that state slices and startup bundles do not pull in simulation code.
 */

/** Max samples per series before decimation. */
export const RING_CAP = 2400;

export interface SeriesPoint {
  t: number;
  v: number;
}

export interface SeriesBuffer {
  t: number[];
  v: number[];
  /** Accept every `stride`-th incoming sample (doubles on each decimation). */
  stride: number;
  /** Incoming-sample counter for the stride filter. */
  counter: number;
  /** True when the buffer was decimated at least once (UI shows "downsampled"). */
  downsampled: boolean;
}

export const buffers = new Map<string, SeriesBuffer>();

export function bufferFor(key: string): SeriesBuffer {
  let b = buffers.get(key);
  if (!b) {
    b = { t: [], v: [], stride: 1, counter: 0, downsampled: false };
    buffers.set(key, b);
  }
  return b;
}

/** Append one live sample (respects the decimation stride + ring cap). */
export function pushSample(key: string, t: number, v: number): void {
  const b = bufferFor(key);
  if (b.counter++ % b.stride !== 0) return;
  b.t.push(t);
  b.v.push(v);
  if (b.t.length >= RING_CAP) {
    // Decimate by 2: keep even indices; future samples accepted at 2x stride.
    const nt: number[] = [];
    const nv: number[] = [];
    for (let i = 0; i < b.t.length; i += 2) {
      nt.push(b.t[i]);
      nv.push(b.v[i]);
    }
    b.t = nt;
    b.v = nv;
    b.stride *= 2;
    b.downsampled = true;
  }
}

/**
 * Replace a series wholesale with an externally produced trajectory (EO
 * transient). REPLACE, not append: a transient run is a complete, coherent
 * time base — interleaving it with live-sim samples would mix epochs. If the
 * trajectory exceeds the cap it is uniformly decimated to fit.
 */
export function ingestSeriesData(key: string, pts: readonly SeriesPoint[]): void {
  const stride = Math.max(1, Math.ceil(pts.length / RING_CAP));
  const b: SeriesBuffer = {
    t: [],
    v: [],
    stride: 1,
    counter: 0,
    downsampled: stride > 1,
  };
  for (let i = 0; i < pts.length; i += stride) {
    const p = pts[i];
    if (Number.isFinite(p.t) && Number.isFinite(p.v)) {
      b.t.push(p.t);
      b.v.push(p.v);
    }
  }
  buffers.set(key, b);
}

/** Read-only view of one series (the LIVE arrays — do not mutate; copy to plot). */
export function getSeries(key: string): {
  readonly t: readonly number[];
  readonly v: readonly number[];
  readonly downsampled: boolean;
} | null {
  const b = buffers.get(key);
  return b ? { t: b.t, v: b.v, downsampled: b.downsampled } : null;
}

export function clearSeries(key: string): void {
  buffers.delete(key);
}

export function clearAllSeries(): void {
  buffers.clear();
}

/** Number of live buffers (test/diagnostic). */
export function seriesCount(): number {
  return buffers.size;
}
